import React from "react";
import styled from "styled-components";
import FullCalendar, {
  DatesSetArg,
  EventClickArg,
} from "@fullcalendar/react";
import enLocale from "@fullcalendar/core/locales/en-gb";
import svLocale from "@fullcalendar/core/locales/sv";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { theme } from "src/config";
import { Cell, Checkbox, Spacer, Sidebar, ItemList } from "src/components";
import { DataType, IItem } from "src/types";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { AuthStore, ItemStore, SpaceStore } from "src/stores";
import { Item } from "src/models";
import { useClickOutside } from "src/util/util";
import { format } from "date-fns";

interface IDashboardCalendarProps {
  authStore?: AuthStore;
  itemStores?: { [type: string]: ItemStore<Item<IItem>, IItem> };  // This is injected so it has to be called match
  spaceStore?: SpaceStore;
}

export const DashboardCalendar = inject("itemStores", "spaceStore", "authStore")(observer((props: IDashboardCalendarProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { itemStores, spaceStore } = props;

  const [showSidebar, setShowSidebar] = React.useState<boolean>(false);
  const toggleSidebar = React.useCallback(() => setShowSidebar(!showSidebar), [showSidebar]);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [date, setDate] = React.useState<string>(undefined);
  const [items, setItems] = React.useState<Item<IItem>[]>([]);
  
  useClickOutside(wrapperRef, () => setIsOpen(false));

  const [filters, setFilters] = React.useState<{ [type: string]: boolean }>({
    [DataType.Question]: true,
    [DataType.Deliverable]: true,
    [DataType.Meeting]: true,
    [DataType.Milestone]: true,
  });

  const filterOptions = [DataType.Meeting, DataType.Question, DataType.Milestone, DataType.Deliverable].map(type => ({
    label: t(type + "_plural"),
    type: type,
    variant: type,
    // @ts-ignore
    color: `${theme.color[(type + "Dark")]}`
  }));


  return (
    <CalendarContainer ref={wrapperRef}>
      <FullCalendar
        defaultAllDay
        firstDay           = {1}
        height             = "auto"
        displayEventTime   = {true}
        handleWindowResize = {true}
        plugins            = {[dayGridPlugin, interactionPlugin]}
        initialView        = "dayGridMonth"
        timeZone           = 'UTC'
        locale             = {i18n.language}
        locales            = {[enLocale, svLocale]}
        events             = {[
          ...(filters.meeting ? itemStores.meeting.getCalendarEvents : []),
          ...(filters.milestone ? itemStores.milestone.getCalendarEvents : []),
          ...(filters.deliverable ? itemStores.deliverable.getCalendarEvents : []),
          ...(filters.question ? itemStores.question.getCalendarEvents : []),
        ].filter(Boolean)}
        eventClick       = {(arg: EventClickArg) => {
          const {start} = arg.event;
          const {items} = arg.event.extendedProps;
          console.log('format this');
          setDate(format(start, 'yyyy-MM-dd'));
          setItems(items);
          setShowSidebar(!showSidebar)
        }}
        weekNumberFormat = {{ week: "numeric" }}
        datesSet         = {(dateInfo: DatesSetArg) => spaceStore.setCurDate(dateInfo.view.currentStart)}
        headerToolbar    = {{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,dayGridWeek"
        }}
      />
      <Spacer size="small" />
      <Row>
        {filterOptions.map((f) => (
          <Checkbox
            color={f.color}
            key={f.label}
            label={f.label}
            variant={f.variant}
            onClick={() => setFilters({ ...filters, [f.type]: !filters[f.type] })}
            checked={filters[f.type]}
          />
        ))}
      </Row>
      <StyledSidebar
        closeOnClickOutside
        overlay
        content={
          <ItemList items={items} date={date} />
         } 
        size="medium"
        anchor="right"
        open={showSidebar}
        onClose={toggleSidebar}
      />
    </CalendarContainer>
  );
}));

const CalendarContainer = styled(Cell)`
  padding: 2rem 0rem;

  .fc td .fc-daygrid-day.fc-day:hover {
    background: ${theme.color.grayLight};
  }

  .fc .fc-daygrid-day.fc-day-today {
    background: none;
    color: ${theme.color.blue9};
    font-weight: ${theme.font.weight.bold};
  }

  .fc-h-event {
    background-color: none;
    padding: 0.5rem;
  }

  .fc-col-header {
    font-size: ${theme.font.size.small};
  }

  .fc-button {
    font-size: ${theme.font.size.small};
  }

  .fc-button-primary {
    color: ${theme.font.color.black};
    background-color: white ;
    border-color:  ${theme.color.primaryBorder};
  }

  .fc-today-button {
    background-color: ${theme.color.slate5};
    color: ${theme.color.slate11};
  }

  .fc-toolbar-title {
    font-size: ${theme.font.size.large};
  }

  .fc .fc-daygrid-day-events {
    font-size: ${theme.font.size.small};
    cursor: pointer;
  }

  .fc-event-title {
    padding: 0.4rem;
  }
`;

const Row = styled(Cell)`
  display: flex;
  height: fit-content;
  flex-direction: row;
  list-style-type: none;
  gap: 1.5rem;
`;

const StyledSidebar = styled(Sidebar)`
  position:         absolute;
  z-index:          100;
  padding:          1rem 1rem;
  max-width:        10rem;
`